import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MeasureField, { useMeasureFieldUnits } from './MeasureField';
import {
  Add,
  Delete,
  Edit,
  EditLocationAlt as MapMarkerIcon,
} from '@mui/icons-material';
import MultipleAddressesSelector from './MultipleAddressesSelector';
import AllocationBar from './AllocationBar';
import AllocationSummary from './AllocationSummary';
import RecipientAutocomplete from './RecipientAutocomplete';
import RecipientWeightSlider from './RecipientWeightSlider';

export const mapDestinationAddress = (team) => {
  return {
    lat: team?.lat,
    lng: team?.lng,
    formattedAddress: team?.formatted_address,
    name: team?.place,
    streetAddress: team?.street_address,
    city: team?.city,
    province: team?.province,
    country: team?.country,
    zipCode: team?.zip_code,
    placeId: team?.place_id,
    timeZoneId: team?.timezone_id,
  };
};

const formatAmount = (value) => {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? number : Number(number.toFixed(2));
};

const parseNonNegativeAmount = (value) => {
  if (value === '') return '';
  if (String(value).includes('-')) return null;
  return Math.max(0, parseFloat(value) || 0);
};

const preventNegativeAmountKey = (event) => {
  if (event.key === '-') {
    event.preventDefault();
  }
};

const stripDraftFlag = ({ isDraft: _isDraft, ...recipient }) => recipient;

const CardFoodType = ({
  handleInputChange,
  isPartialRescue,
  errors,
  recipients,
  item,
  disabledRecipients,
  value,
  onChange,
  destinationAddresses = [],
  onAddressChange,
  deliveryOption,
  colorMap = new Map(),
}) => {
  const [addressSelectorOpen, setAddressSelectorOpen] = useState(false);
  const [selectedRecipientTeamId, setSelectedRecipientTeamId] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [hasDraftRecipient, setHasDraftRecipient] = useState(false);
  const prevTotalRef = useRef(null);

  const getError = (key) => {
    return errors?.foodTypes?.[item.id]?.[key]?.message;
  };

  const { t } = useTranslation('translation');
  const { WEIGHT_UNITS, defaultWeightUnit } = useMeasureFieldUnits();
  const currentValue = value[item.id];
  const FoodTypeIcon = item.icon;
  const assignedRecipients = currentValue?.assignedRecipients ?? [
    {
      teamId: 'unassigned',
      reservedWeight: 0,
    },
  ];
  const totalAmount = parseFloat(currentValue?.value) || 0;
  const selectedRecipients = assignedRecipients
    .filter(
      (recipient) => recipient.teamId && recipient.teamId !== 'unassigned'
    )
    .map(stripDraftFlag);
  const editableRecipients = hasDraftRecipient
    ? [...selectedRecipients, { teamId: '', reservedWeight: 0, isDraft: true }]
    : selectedRecipients;
  const reservedAmount = selectedRecipients.reduce(
    (acc, curr) => acc + parseFloat(curr.reservedWeight || 0),
    0
  );
  const openAmount = Math.max(0, totalAmount - reservedAmount);
  const openAmountLabel = selectedRecipients.length
    ? t('donations.available-to-others')
    : t('donations.available-to-recipients');
  const currentAmount =
    reservedAmount > totalAmount ? reservedAmount : reservedAmount + openAmount;
  const effectiveAssignedRecipients =
    openAmount > 0
      ? [
          ...selectedRecipients,
          {
            teamId: 'unassigned',
            reservedWeight: openAmount,
          },
        ]
      : selectedRecipients;
  const canAddRecipient =
    !currentValue?.disabled && openAmount > 0 && !hasDraftRecipient;
  // A recipient with a completed (or in-flight) rescue locks this food type:
  // it can't be removed, only its total adjusted above the locked portion.
  const hasLockedRecipient = (currentValue?.assignedRecipients || []).some(
    (recipient) => recipient.disabled
  );

  const minReservableWeight = currentValue?.minReservableWeight ?? '';
  const maxReservableWeight = currentValue?.maxReservableWeight ?? '';
  const defaultValues = {
    value: 0,
    unit: defaultWeightUnit.value,
    ...(isPartialRescue
      ? {
          minReservableWeight: 0,
          maxReservableWeight: 0,
          maxReservableWeightManuallyChanged: false,
          assignedRecipients: [
            {
              teamId: 'unassigned',
              reservedWeight: 0,
            },
          ],
        }
      : {}),
  };

  const withDerivedOpenRecipient = (
    recipientsToSync = [],
    amount = totalAmount
  ) => {
    const selected = recipientsToSync
      .filter(
        (recipient) => recipient.teamId && recipient.teamId !== 'unassigned'
      )
      .map(stripDraftFlag);
    const selectedTotal = selected.reduce(
      (acc, recipient) => acc + parseFloat(recipient.reservedWeight || 0),
      0
    );
    const openWeight = Math.max(0, amount - selectedTotal);

    return openWeight > 0
      ? [
          ...selected,
          {
            teamId: 'unassigned',
            reservedWeight: openWeight,
          },
        ]
      : selected;
  };

  const syncPartialFoodTypeValue = (nextValue) => {
    if (!isPartialRescue) return nextValue;

    const nextTotal = parseFloat(nextValue.value) || 0;
    const previousTotal = parseFloat(currentValue?.value) || 0;
    const currentMax = parseFloat(nextValue.maxReservableWeight) || 0;
    const maxManuallyChanged = Boolean(
      nextValue.maxReservableWeightManuallyChanged
    );
    const syncedValue = {
      ...nextValue,
      assignedRecipients: withDerivedOpenRecipient(
        nextValue.assignedRecipients || [],
        nextTotal
      ),
    };

    if (nextTotal <= 0) {
      if (!maxManuallyChanged) {
        syncedValue.maxReservableWeight = 0;
      }
      return syncedValue;
    }

    if (currentMax > nextTotal) {
      syncedValue.maxReservableWeight = nextTotal;
    } else if (
      !maxManuallyChanged &&
      (currentMax === 0 || currentMax === previousTotal)
    ) {
      syncedValue.maxReservableWeight = nextTotal;
    }

    if (!parseFloat(syncedValue.minReservableWeight)) {
      syncedValue.minReservableWeight = 1;
    }

    return syncedValue;
  };

  // Auto-prefill min/max when total weight changes within partial mode
  useEffect(() => {
    if (!isPartialRescue || !currentValue) return;
    const newTotal = parseFloat(currentValue.value) || 0;
    const prevTotal = prevTotalRef.current;
    prevTotalRef.current = newTotal;

    // Only prefill on weight change, not on initial mount (toggle handled by useGenerateFoodTypesObj)
    if (prevTotal !== null && newTotal > 0 && newTotal !== prevTotal) {
      let nextValue = null;
      const currentMax = parseFloat(currentValue.maxReservableWeight) || 0;
      const maxManuallyChanged = Boolean(
        currentValue.maxReservableWeightManuallyChanged
      );
      if (
        currentMax > newTotal ||
        (!maxManuallyChanged && (currentMax === 0 || currentMax === prevTotal))
      ) {
        nextValue = {
          ...(nextValue || currentValue),
          minReservableWeight: 1,
          maxReservableWeight: newTotal,
        };
      }

      const currentRecipients = currentValue.assignedRecipients || [];
      const nextRecipients = withDerivedOpenRecipient(
        currentRecipients,
        newTotal
      );

      if (
        JSON.stringify(nextRecipients) !== JSON.stringify(currentRecipients)
      ) {
        nextValue = {
          ...(nextValue || currentValue),
          assignedRecipients: nextRecipients,
        };
      }

      if (nextValue) {
        handleInputChange(item.id)(nextValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue?.value]);

  const handleChange = (key, value) => {
    if (currentValue?.disabled) return;
    if (!currentValue) {
      handleInputChange(item.id)({
        ...defaultValues,
        [key]: value,
      });
    } else {
      handleInputChange(item.id)({ ...currentValue, [key]: value });
    }
  };

  const handleAddressChange = ({ teamId, newAddress }) => {
    if (onAddressChange) {
      onAddressChange({ teamId, newAddress });
    }
  };

  const openAddressSelector = (teamId) => {
    setSelectedRecipientTeamId(teamId);
    setAddressSelectorOpen(true);
  };

  const getRecipientByTeamId = (teamId) => {
    return recipients.find((r) => r.team_id === teamId);
  };

  const getCurrentAddressByTeamId = (teamId) => {
    const customAddress = destinationAddresses.find(
      (addr) => addr.teamId === teamId
    );

    if (customAddress && customAddress.location) {
      return customAddress.location;
    }

    return mapDestinationAddressForTeam(teamId);
  };

  const mapDestinationAddressForTeam = (teamId) => {
    const recipient = getRecipientByTeamId(teamId);
    return mapDestinationAddress(recipient);
  };

  const isCustomAddress = (teamId) => {
    const recipientData = getRecipientByTeamId(teamId);

    const addressEntry = destinationAddresses.find(
      (addr) => addr.teamId === teamId
    );

    if (!recipientData || !addressEntry || !addressEntry.location) return false;

    return (
      addressEntry.location.lat !== recipientData.lat ||
      addressEntry.location.lng !== recipientData.lng
    );
  };

  const computeRemainingForRecipient = (idx) => {
    const othersTotal = editableRecipients.reduce((acc, r, i) => {
      if (i === idx) return acc;
      if (!r.teamId) return acc;
      return acc + parseFloat(r.reservedWeight || 0);
    }, 0);
    return Math.max(0, totalAmount - othersTotal);
  };

  const handleRecipientSelect = (idx, newTeamId) => {
    const recipient = editableRecipients[idx];

    if (!newTeamId) {
      if (recipient?.isDraft) {
        setHasDraftRecipient(false);
      }
      return;
    }

    const newAssigned = [...selectedRecipients];
    const nextRecipient = {
      ...stripDraftFlag(recipient),
      teamId: newTeamId,
    };

    if (parseFloat(nextRecipient.reservedWeight || 0) === 0) {
      nextRecipient.reservedWeight = computeRemainingForRecipient(idx);
    }

    if (recipient?.isDraft) {
      newAssigned.push(nextRecipient);
    } else {
      newAssigned[idx] = nextRecipient;
    }
    setHasDraftRecipient(false);
    handleChange('assignedRecipients', withDerivedOpenRecipient(newAssigned));
  };

  const unitLabel = currentValue?.unit || defaultWeightUnit.value;
  const parsedMinReservableWeight =
    parseFloat(currentValue?.minReservableWeight) || 0;
  const parsedMaxReservableWeight =
    parseFloat(currentValue?.maxReservableWeight) || 0;
  const enteredMinForClaimRule = Math.max(1, parsedMinReservableWeight || 1);
  // Backend clamps min to the unassigned pool at save; preview the same.
  const minForClaimRule =
    openAmount > 0
      ? Math.min(enteredMinForClaimRule, openAmount)
      : enteredMinForClaimRule;
  const maxForClaimRule = parsedMaxReservableWeight || totalAmount;
  const availableLimitAmount = openAmount > 0 ? openAmount : totalAmount;
  const claimRuleSummary =
    parsedMaxReservableWeight > 0 || availableLimitAmount > 0
      ? t('donations.claim-rule-between', {
          min: formatAmount(minForClaimRule),
          max: formatAmount(maxForClaimRule),
          unit: unitLabel,
        })
      : t('donations.claim-rule-at-least', {
          amount: formatAmount(minForClaimRule),
          unit: unitLabel,
        });
  const capTotalLimit = (nextValue) => {
    if (nextValue === '') return '';
    if (totalAmount <= 0) return nextValue;
    return Math.min(nextValue, totalAmount);
  };

  const useAnyAmountRule = () => {
    handleInputChange(item.id)({
      ...currentValue,
      minReservableWeight: 1,
      maxReservableWeight: totalAmount,
      maxReservableWeightManuallyChanged: false,
    });
  };

  return (
    <>
      <Stack
        direction="column"
        gap={0}
        sx={{
          border: (t) => `1px solid ${t.palette.divider}`,
          borderColor: (t) =>
            errors?.foodTypes?.[item.id] ? 'error.main' : t.palette.divider,
          transition: 'all 0.2s ease',
          borderRadius: '12px',
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        {isPartialRescue ? (
          /* Partial: wraps on mobile */
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'auto minmax(0, 1fr) auto',
                sm: 'auto minmax(0, 1fr) minmax(190px, 220px) auto',
              },
              gridTemplateAreas: {
                xs: '"icon title delete" "amount amount amount"',
                sm: '"icon title amount delete"',
              },
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 1.5,
              px: 2.5,
              py: 2,
              backgroundColor: 'grey.50',
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            {FoodTypeIcon ? (
              <Box
                sx={{
                  gridArea: 'icon',
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                }}
              >
                <FoodTypeIcon sx={{ width: 24, height: 24 }} />
              </Box>
            ) : (
              <Box
                component="img"
                src={item.image}
                sx={{
                  gridArea: 'icon',
                  width: 40,
                  height: 40,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="subtitle1"
              sx={{ gridArea: 'title', fontWeight: 600, minWidth: 0 }}
            >
              {item.title}
            </Typography>
            <Box
              sx={{
                gridArea: 'amount',
                width: '100%',
                maxWidth: { xs: 'none', sm: 220 },
                justifySelf: { xs: 'stretch', sm: 'end' },
              }}
            >
              <MeasureField
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  },
                }}
                label={t('donations.amount')}
                units={WEIGHT_UNITS}
                value={value[item.id]}
                allowNegative={false}
                error={!!getError('value')}
                helperText={getError('value')}
                onChange={(val) => {
                  handleInputChange(item.id)(
                    syncPartialFoodTypeValue({
                      ...(!currentValue ? defaultValues : currentValue),
                      ...val,
                    })
                  );
                }}
              />
            </Box>
            <IconButton
              disabled={
                currentValue?.disabled ||
                hasLockedRecipient ||
                Object.keys(value).length === 1
              }
              color="secondary"
              size="small"
              sx={{ gridArea: 'delete', justifySelf: 'end' }}
              onClick={() => {
                const newFoodTypes = { ...value };
                delete newFoodTypes[item.id];
                onChange(newFoodTypes);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          /* Non-partial: stacked vertical layout (half width card) */
          <Stack
            direction="column"
            alignItems="center"
            gap={2}
            sx={{
              px: 2.5,
              py: 3,
              position: 'relative',
            }}
          >
            <IconButton
              disabled={
                currentValue?.disabled || Object.keys(value).length === 1
              }
              color="secondary"
              size="small"
              sx={{ position: 'absolute', top: 6, right: 6 }}
              onClick={() => {
                const newFoodTypes = { ...value };
                delete newFoodTypes[item.id];
                onChange(newFoodTypes);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={item.image}
              sx={{
                width: 120,
                height: 80,
                objectFit: 'contain',
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.title}
            </Typography>
            <MeasureField
              sx={{
                width: '100%',
              }}
              label={t('common.weight')}
              units={WEIGHT_UNITS}
              value={value[item.id]}
              allowNegative={false}
              error={!!getError('value')}
              helperText={getError('value')}
              onChange={(val) =>
                handleInputChange(item.id)({
                  ...(!currentValue ? defaultValues : currentValue),
                  ...val,
                })
              }
            />
          </Stack>
        )}

        {/* ── Partial rescue body — hidden until weight is entered ── */}
        {isPartialRescue && totalAmount > 0 ? (
          <Stack sx={{ px: 2.5, py: 2.25 }} direction="column" gap={2}>
            {/* Allocation visualization */}
            {totalAmount > 0 && (
              <Stack gap={1}>
                <AllocationBar
                  assignedRecipients={effectiveAssignedRecipients}
                  totalWeight={totalAmount}
                  recipients={recipients}
                  unit={unitLabel}
                  colorMap={colorMap}
                />
                <AllocationSummary
                  currentAmount={currentAmount}
                  totalAmount={totalAmount}
                  assignedRecipients={effectiveAssignedRecipients}
                  unit={unitLabel}
                />
              </Stack>
            )}

            {/* Recipients section */}
            <Stack gap={1}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={1}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                  gap={{ xs: 0.25, sm: 1 }}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('donations.recipients')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('donations.recipient-plan-help')}
                  </Typography>
                </Stack>
                {canAddRecipient && (
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    sx={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHasDraftRecipient(true);
                    }}
                  >
                    {t('donations.add-recipient')}
                  </Button>
                )}
              </Stack>

              <Stack
                direction="column"
                spacing={0}
                divider={<Divider sx={{ mx: 0.5 }} />}
                sx={{ width: '100%' }}
              >
                {editableRecipients.map((recipient, idx) => {
                  const handleRecipientChange = (key, val) => {
                    const newAssignedRecipients = [...editableRecipients];
                    newAssignedRecipients[idx] = {
                      ...newAssignedRecipients[idx],
                      [key]: val,
                    };

                    handleChange(
                      'assignedRecipients',
                      withDerivedOpenRecipient(newAssignedRecipients)
                    );
                  };

                  const errorList = Object.values(
                    errors?.foodTypes?.[item.id]?.assignedRecipients?.[idx] ||
                      {}
                  ).map((error) =>
                    typeof error === 'string' ? error : error?.message
                  );

                  const hasRecipient = Boolean(recipient.teamId);
                  const sliderMax = computeRemainingForRecipient(idx);
                  const recipientColor =
                    colorMap.get(recipient.teamId) || '#DFE3E8';

                  return (
                    <Stack
                      key={recipient.teamId || `draft-${idx}`}
                      direction="column"
                      sx={{
                        py: 1.5,
                        px: 0.5,
                      }}
                    >
                      {/* Row 1: Recipient selector (full width) + actions */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{ width: '100%' }}
                      >
                        {/* Color bar */}
                        <Box
                          sx={{
                            width: 3,
                            alignSelf: 'stretch',
                            borderRadius: 1,
                            backgroundColor: recipientColor,
                            flexShrink: 0,
                          }}
                        />

                        {/* Recipient dropdown — full width */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <RecipientAutocomplete
                            value={recipient.teamId}
                            onChange={(newTeamId) =>
                              handleRecipientSelect(idx, newTeamId)
                            }
                            recipients={recipients}
                            assignedRecipients={editableRecipients}
                            disabledRecipients={disabledRecipients}
                            currentIndex={idx}
                            disabled={recipient?.disabled}
                            label={t('donations.recipient')}
                            placeholder={t('donations.search-recipients')}
                            includeUnassignedOption={false}
                          />
                        </Box>

                        {deliveryOption === 0 && (
                          <IconButton
                            sx={{ flexShrink: 0 }}
                            disabled={!hasRecipient}
                            color={
                              !hasRecipient
                                ? 'warning'
                                : isCustomAddress(recipient.teamId)
                                  ? 'secondary'
                                  : 'default'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasRecipient) {
                                openAddressSelector(recipient.teamId);
                              }
                            }}
                            size="small"
                          >
                            <MapMarkerIcon fontSize="small" />
                          </IconButton>
                        )}

                        <IconButton
                          disabled={recipient?.disabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (recipient?.isDraft) {
                              setHasDraftRecipient(false);
                            } else {
                              handleChange(
                                'assignedRecipients',
                                withDerivedOpenRecipient(
                                  selectedRecipients.filter((_, i) => i !== idx)
                                )
                              );
                            }
                          }}
                          color="secondary"
                          sx={{
                            flexShrink: 0,
                          }}
                          size="small"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>

                      {/* Row 2: Weight slider (full width, below recipient) */}
                      <Stack sx={{ mt: 1, pl: 2 }}>
                        <RecipientWeightSlider
                          value={parseFloat(recipient.reservedWeight) || 0}
                          max={sliderMax}
                          onChange={(newWeight) =>
                            handleRecipientChange('reservedWeight', newWeight)
                          }
                          disabled={recipient?.disabled || !hasRecipient}
                          color={recipientColor}
                          label={t('donations.amount')}
                          unit={unitLabel}
                        />
                      </Stack>

                      {/* Custom address note */}
                      {hasRecipient && isCustomAddress(recipient.teamId) && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ mt: 0.5, pl: 2 }}
                        >
                          {t('donations.custom-address')}:{' '}
                          {
                            getCurrentAddressByTeamId(recipient.teamId)
                              .formattedAddress
                          }
                        </Typography>
                      )}

                      {/* Errors */}
                      {errorList.length > 0 && (
                        <Stack
                          direction="column"
                          spacing={0.5}
                          sx={{ mt: 0.5, pl: 2 }}
                        >
                          {errorList.map((error, i) => (
                            <Typography key={i} color="error" variant="caption">
                              {error}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  );
                })}

                {openAmount > 0 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{
                      py: 1.25,
                      px: 0.5,
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Box
                        sx={{
                          width: 3,
                          alignSelf: 'stretch',
                          borderRadius: 1,
                          backgroundColor: '#DFE3E8',
                          flexShrink: 0,
                        }}
                      />
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {openAmountLabel}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        pl: { xs: 2, sm: 0 },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatAmount(openAmount)} {unitLabel}
                    </Typography>
                  </Stack>
                )}

                {getError('assignedRecipients') ? (
                  <Typography color="error" variant="caption">
                    {getError('assignedRecipients')}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>

            <Stack
              gap={rulesOpen ? 1 : 0}
              sx={{
                borderTop: (t) => `1px solid ${t.palette.divider}`,
                pt: 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    component="span"
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                  >
                    {t('donations.available-amount-limits')}:
                  </Box>{' '}
                  {claimRuleSummary}
                </Typography>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  startIcon={rulesOpen ? null : <Edit fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    flexShrink: 0,
                    minWidth: 'auto',
                  }}
                  onClick={() => setRulesOpen((open) => !open)}
                >
                  {rulesOpen ? t('form.actions.done') : t('form.actions.edit')}
                </Button>
              </Stack>

              <Collapse in={rulesOpen}>
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('donations.available-amount-limits-help', {
                      amount: formatAmount(availableLimitAmount),
                      unit: unitLabel,
                    })}
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems="flex-start"
                    gap={2}
                  >
                    <TextField
                      label={t('donations.min-available-pickup')}
                      size="small"
                      type="number"
                      disabled={hasLockedRecipient}
                      error={!!getError('minReservableWeight')}
                      helperText={getError('minReservableWeight')}
                      value={minReservableWeight}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const nextValue = parseNonNegativeAmount(
                          e.target.value
                        );
                        if (nextValue === null) return;
                        handleInputChange(item.id)({
                          ...currentValue,
                          minReservableWeight: capTotalLimit(nextValue),
                          minReservableWeightManuallyChanged: true,
                        });
                      }}
                      onKeyDown={preventNegativeAmountKey}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: totalAmount || undefined,
                        },
                      }}
                      sx={{
                        flex: 1,
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { sm: 190 },
                      }}
                    />
                    <TextField
                      label={t('donations.max-available-pickup')}
                      size="small"
                      type="number"
                      disabled={hasLockedRecipient}
                      error={!!getError('maxReservableWeight')}
                      helperText={getError('maxReservableWeight')}
                      value={maxReservableWeight}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const nextValue = parseNonNegativeAmount(
                          e.target.value
                        );
                        if (nextValue === null) return;
                        handleInputChange(item.id)({
                          ...currentValue,
                          maxReservableWeight: capTotalLimit(nextValue),
                          maxReservableWeightManuallyChanged: true,
                        });
                      }}
                      onKeyDown={preventNegativeAmountKey}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: totalAmount || undefined,
                        },
                      }}
                      sx={{
                        flex: 1,
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { sm: 190 },
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      disabled={hasLockedRecipient}
                      sx={{
                        textTransform: 'none',
                        minHeight: 40,
                        whiteSpace: 'nowrap',
                      }}
                      onClick={useAnyAmountRule}
                    >
                      {t('donations.use-any-amount-rule')}
                    </Button>
                  </Stack>
                </Stack>
              </Collapse>
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      {addressSelectorOpen && selectedRecipientTeamId && (
        <MultipleAddressesSelector
          open={addressSelectorOpen}
          onClose={() => {
            setAddressSelectorOpen(false);
            setSelectedRecipientTeamId(null);
          }}
          recipient={getRecipientByTeamId(selectedRecipientTeamId)}
          defaultAddress={mapDestinationAddressForTeam(selectedRecipientTeamId)}
          value={getCurrentAddressByTeamId(selectedRecipientTeamId)}
          onChange={(newAddress) => {
            handleAddressChange({
              teamId: selectedRecipientTeamId,
              newAddress,
            });
          }}
        />
      )}
    </>
  );
};

export default CardFoodType;
